/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useContext, createContext, PropsWithChildren, useEffect } from "react";
import type { Product as IProduct } from "~api/stream/interfaces";
import { useSocketContext } from "~contexts/Socket";
import { useStreamContext } from "~contexts/Stream";

interface IRealtimeProductsContext {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  selectedProducts: IProduct[];
  setSelectedProducts: (value: IProduct[]) => void;
  productList: IProduct[];
  setProductList: (value: IProduct[]) => void;
}

const RealtimeProductsContext = createContext<IRealtimeProductsContext>({
  searchTerm: "",
  setSearchTerm: () => "",
  selectedProducts: [],
  setSelectedProducts: () => [],
  productList: [],
  setProductList: () => [],
});

export const RealtimeProductsContextProvider = ({ children }: PropsWithChildren) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedProducts, setSelectedProducts] = useState<IProduct[]>([]);
  const [productList, setProductList] = useState<IProduct[]>([]);

  useEffect(() => {
    const selectedKeys = selectedProducts.map(
      (p) => p.provider?.productId || String(p.id)
    );
    const updatedProductList = productList.map((product) => ({
      ...product,
      isChecked: selectedKeys.includes(product.provider?.productId || String(product.id)),
    }));
    setProductList(updatedProductList);
  }, [selectedProducts]);

  const { socket } = useSocketContext();
  const { stream } = useStreamContext();
  const isRerun = stream?.video?.isRerun;

  useEffect(() => {
    if (!socket) return;

    const normalize = (payload: unknown): IProduct[] => {
      if (Array.isArray(payload)) return payload as IProduct[];
      const maybe = payload as { products?: IProduct[] } | null;
      if (Array.isArray(maybe?.products)) return maybe!.products;
      return [];
    };

    const hydrate = (payload: unknown) => {
      if (isRerun) return; // em rerun o front usa timeline, não realtime
      setSelectedProducts(normalize(payload));
    };

    socket.on("currentProductData", hydrate); // resposta de getCurrentProduct (reidratação)
    socket.on("updateRealTimeProducts", hydrate); // broadcast de outro admin (sync ao vivo)

    // Corrida: se o socket já conectou antes deste efeito montar, o connect já
    // disparou getCurrentProduct sem listener anexado. Re-pedimos (leitura idempotente).
    if (socket.connected) socket.emit("getCurrentProduct");

    return () => {
      socket.off("currentProductData", hydrate);
      socket.off("updateRealTimeProducts", hydrate);
    };
  }, [socket, isRerun]);

  return (
    <RealtimeProductsContext.Provider
      value={{
        searchTerm,
        setSearchTerm,
        selectedProducts,
        setSelectedProducts,
        productList,
        setProductList,
      }}
    >
      {children}
    </RealtimeProductsContext.Provider>
  );
};

export const useRealtimeProductsContext = () => {
  const context = useContext(RealtimeProductsContext);
  return context;
};
